import { PageController } from '@client/components/_core/layout/PageController';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { match } from 'ts-pattern';
import { type AppSetting, AppSettingForm } from './AppSettingForm';

export const Route = createFileRoute('/admin-console/advanced/app-settings/all/edit/$name')({
    component: EditAppSetting,
    params: {
        parse({ name }) {
            return { name: decodeURIComponent(name) };
        },
    },
    staticData: {
        presentation: 'overlay',
    },
});

function EditAppSetting() {
    const { name: settingName } = Route.useParams();
    const { trpc, trpcUtils } = useTrpc();
    const navigate = useNavigate();
    const [open, setOpen] = useState(true);
    const { isReadOnly } = useAdminModulePermissions('system');

    const {
        data: appSetting,
        isPending: isLoading,
        error,
    } = trpc.admin.appSetting.getAppSetting.useQuery({ name: settingName }, { refetchOnWindowFocus: false });

    const { mutate: updateAppSetting, isPending: isUpdating } = trpc.admin.appSetting.updateAppSetting.useMutation({
        onError: (error) => {
            errorMessage.show(error);
        },
    });

    const handleSubmit = (values: AppSetting) => {
        if (!settingName) return;
        updateAppSetting(
            {
                name: settingName,
                value: values.value ?? '',
                format: values.format ?? 'PLAIN_TEXT',
            },
            {
                onSuccess: () => {
                    message.success('App setting updated');
                    trpcUtils.admin.appSetting.listAppSettings.invalidate();
                    setOpen(false);
                },
            },
        );
    };

    return (
        <PageController route={Route}>
            <AppSettingForm
                open={open}
                mode="edit"
                data={appSetting ?? undefined}
                state={match({ error, isUpdating, isLoading })
                    .with({ isUpdating: true }, () => 'submitting' as const)
                    .with({ isLoading: true }, () => 'loading' as const)
                    .otherwise(() => 'ready' as const)}
                onSubmit={handleSubmit}
                onCloseFinished={() => {
                    navigate({ to: '/admin-console/advanced/app-settings/all' });
                }}
                isReadOnly={isReadOnly}
            />
        </PageController>
    );
}
